import { useFetcher } from "@remix-run/react";
import { useContext, useEffect } from "react";
import { BookingFormContext } from "~/components/booking/ContextProvider";
import { route } from "~/utils/route";
import { type loader as timeSelectLoader } from "~/routes/resources+/book+/time-select";
import * as RadioCard from "~/components/ui/radio-card";
import { Label } from "~/components/ui/label";

export function TimeSelectInput() {
  const context = useContext(BookingFormContext);
  if (!context) {
    throw new Error("BookingFormContext is not provided");
  }

  const {
    selectedTime,
    setSelectedTime,
    selectedDate,
    selectedBarberId,
    selectedServiceIds,
  } = context;

  const timeSelectFetcher = useFetcher<typeof timeSelectLoader>();

  useEffect(() => {
    if (!selectedBarberId || !selectedServiceIds.length || !selectedDate) {
      return;
    }

    const url = route("/resources/book/time-select");
    const searchParams = new URLSearchParams();
    searchParams.set("barberId", selectedBarberId);
    selectedServiceIds.forEach((serviceId) => {
      searchParams.append("serviceIds", serviceId);
    });
    searchParams.set("date", selectedDate);

    timeSelectFetcher.load(url + "?" + searchParams.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBarberId, selectedServiceIds, selectedDate]);

  const timeSlots = timeSelectFetcher.data?.timeSlots;

  useEffect(() => {
    // If an event stream is triggered and the booking is no longer available, deselect it.
    if (
      timeSlots &&
      !timeSlots.includes(selectedTime as `${string}:${string}`)
    ) {
      setSelectedTime("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeSlots]);

  return (
    <div>
      <Label htmlFor="time-select" className="px-6">
        Time
      </Label>
      <div className="mt-2" />
      <RadioCard.Root
        id="time-select"
        className="px-6"
        onValueChange={(value) => {
          setSelectedTime(value);
        }}
        value={selectedTime}
      >
        {!selectedBarberId || !selectedServiceIds.length || !selectedDate ? (
          <RadioCard.Item disabled value="" className="w-full">
            <RadioCard.Indicator />
            <span>Complete fields above</span>
          </RadioCard.Item>
        ) : timeSlots && timeSlots.length > 0 ? (
          timeSelectFetcher.data?.timeSlots.map((time) => (
            <RadioCard.Item
              disabled={timeSelectFetcher.state !== "idle"}
              key={time}
              value={time}
            >
              <RadioCard.Indicator />
              <span>{time}</span>
            </RadioCard.Item>
          ))
        ) : (
          <RadioCard.Item disabled value="">
            <RadioCard.Indicator />
            <span>No available time slots</span>
          </RadioCard.Item>
        )}
      </RadioCard.Root>
    </div>
  );
}
